import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

export default function PublicAgreement() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | signed | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    signer_name: "",
    business_name: "",
    signed_date: new Date().toISOString().slice(0, 10),
    agree: false,
  });
  const [tier, setTier] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/public/agreement/${token}`)
      .then((r) => {
        setData(r.data);
        if (r.data.current_tier) setTier(r.data.current_tier);
        if (r.data.already_signed) setState("signed");
        else { setState("ready"); setForm((f) => ({ ...f, business_name: r.data.org_name || "" })); }
      })
      .catch((e) => { setError(e.response?.data?.detail || "This link is invalid."); setState("error"); });
  }, [token]);

  const sign = async () => {
    if (!form.agree) { toast.error("Please tick 'I Agree' to sign."); return; }
    if (!form.signer_name.trim() || !form.business_name.trim() || !form.signed_date) {
      toast.error("Full name, business name and date are all required."); return;
    }
    if ((data?.tiers || []).length > 0 && !tier) { toast.error("Please choose a plan to continue."); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/agreement/${token}/sign`, { ...form, tier: tier || null });
      setState("signed");
      toast.success("Agreement signed — thank you!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record your signature.");
    } finally { setSubmitting(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#0E0F11] text-zinc-100 flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-7 h-7 text-[#1E88E5]" />
          <div>
            <div className="text-xl font-black tracking-tight text-[#1E88E5]">ArmorBatch Global</div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Software Licence Agreement</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );

  if (state === "loading") return (
    <Shell><div className="flex items-center gap-2 text-zinc-400" data-testid="agreement-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading agreement…</div></Shell>
  );

  if (state === "error") return (
    <Shell><div className="border border-red-800 bg-red-950/40 text-red-200 p-6" data-testid="agreement-error">{error}</div></Shell>
  );

  if (state === "signed") return (
    <Shell>
      <div className="border border-green-800 bg-green-950/30 p-8 text-center" data-testid="agreement-signed">
        <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
        <div className="text-lg font-bold mb-1">Agreement signed</div>
        <p className="text-zinc-400 text-sm">{data?.org_name ? `${data.org_name}'s ` : "Your "}account is now fully activated. You can close this window and sign in to ArmorBatch.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      {(data?.tiers || []).length > 0 && (
        <div className="border border-zinc-800 bg-zinc-950/60 p-6 mb-5" data-testid="agreement-tier-picker">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold mb-1">Choose your plan</div>
          <p className="text-xs text-zinc-500 mb-4">Pick the subscription that fits your fleet — you can change it any time.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {data.tiers.map((t) => {
              const sel = tier === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`tier-option-${t.key}`}
                  onClick={() => setTier(t.key)}
                  className={`text-left border p-4 transition-colors ${sel ? "border-[#1E88E5] bg-[#1E88E5]/10 ring-1 ring-[#1E88E5]" : "border-zinc-700 bg-transparent hover:border-zinc-500"}`}
                >
                  <div className="text-sm font-black text-zinc-100">{t.name}</div>
                  <div className="text-2xl font-black text-[#1E88E5] mt-1">${Number(t.price || 0).toLocaleString()}<span className="text-[11px] text-zinc-400 font-normal">/mo</span></div>
                  <div className="text-[11px] text-zinc-500 mt-1">{t.truck_max == null ? `${t.truck_min}+ trucks` : `${t.truck_min}–${t.truck_max} trucks`}</div>
                  <ul className="mt-3 space-y-1">
                    {(t.features || []).slice(0, 5).map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-[11px] text-zinc-300">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                    {(t.features || []).length > 5 && (
                      <li className="text-[10px] text-zinc-500">+ {t.features.length - 5} more</li>
                    )}
                  </ul>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="border border-zinc-800 bg-zinc-950/60 p-6 mb-5 max-h-[52vh] overflow-auto whitespace-pre-wrap text-sm leading-relaxed text-zinc-200" data-testid="agreement-text">
        {data?.text}
      </div>
      <div className="border border-zinc-800 bg-zinc-900/60 p-6 space-y-4" data-testid="agreement-form">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400 font-bold">Accept &amp; Sign</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Full name</label>
            <input data-testid="sign-name" value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })}
              className="w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:border-[#1E88E5] outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Business name</label>
            <input data-testid="sign-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
              className="w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:border-[#1E88E5] outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Date</label>
            <input data-testid="sign-date" type="date" value={form.signed_date} onChange={(e) => setForm({ ...form, signed_date: e.target.value })}
              className="w-full bg-transparent border border-zinc-700 px-3 py-2 text-sm focus:border-[#1E88E5] outline-none" />
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
          <input data-testid="sign-agree" type="checkbox" checked={form.agree} onChange={(e) => setForm({ ...form, agree: e.target.checked })} className="mt-1" />
          I confirm I am authorised to sign on behalf of the business named above, and I agree to the terms of this Software Licence Agreement.
        </label>
        <button data-testid="sign-submit" onClick={sign} disabled={submitting}
          className="w-full bg-[#1E88E5] hover:bg-[#1976D2] text-white font-bold py-3 uppercase tracking-widest text-sm disabled:opacity-50">
          {submitting ? "Signing…" : "I Agree and Sign"}
        </button>
        <p className="text-[10px] text-zinc-500">Your name, the time of signing and your IP address are recorded as proof of acceptance.</p>
      </div>
    </Shell>
  );
}
